import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { History } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EntityManageSection } from '@/components/ui/entity-manage-section'
import { authorizedFetch } from '@/lib/api'
import {
  composeThreadHistory,
  THREAD_HISTORY_INITIAL_CAP,
  type ThreadHistoryFeedback,
  type ThreadHistoryMessage,
} from '@/utils/thread-history'
import {
  navigateWithThreadPreservation,
  withAgentHighlight,
} from '@/utils/thread-navigation'
import { RelativeTime } from './relative-time'

type ThreadFileRow = {
  id: string
  original_name: string
  created_at?: string
}

export type ThreadHistorySectionProps = {
  threadId: string
  messages?: ThreadHistoryMessage[] | null
  feedbackItems?: ThreadHistoryFeedback[] | null
}

export function ThreadHistorySection({
  threadId,
  messages,
  feedbackItems,
}: ThreadHistorySectionProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [expanded, setExpanded] = useState(false)

  const { data: files = [] } = useQuery<ThreadFileRow[]>({
    queryKey: ['thread-files', threadId],
    queryFn: async () => {
      const r = await authorizedFetch(`/api/files/threads/${threadId}/files`)
      if (!r.ok) throw new Error('Failed to load files')
      const data = await r.json()
      return Array.isArray(data) ? (data as ThreadFileRow[]) : []
    },
  })

  const rows = useMemo(
    () =>
      composeThreadHistory({
        messages,
        files,
        feedbackItems,
      }),
    [messages, files, feedbackItems]
  )

  const visible = expanded ? rows : rows.slice(0, THREAD_HISTORY_INITIAL_CAP)
  const hiddenCount = rows.length - visible.length

  return (
    <EntityManageSection title="History" leadingIcon={History}>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
      ) : (
        <div className="space-y-3">
          <ul className="space-y-3">
            {visible.map((row) => {
              const pathToOpen =
                row.target_path && row.focus_key
                  ? withAgentHighlight(row.target_path, row.focus_key)
                  : row.target_path

              return (
                <li key={row.id} className="flex flex-col gap-1 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <RelativeTime
                      value={row.at}
                      className="text-xs text-muted-foreground tabular-nums"
                    />
                    <Badge variant="outline" className="text-xs">
                      {row.badge}
                    </Badge>
                  </div>
                  <p className="text-foreground">{row.primary}</p>
                  {row.secondary ? (
                    <p className="text-xs text-muted-foreground">{row.secondary}</p>
                  ) : null}
                  {pathToOpen ? (
                    <button
                      type="button"
                      className="w-fit text-xs text-muted-foreground underline-offset-2 hover:underline"
                      onClick={() =>
                        navigateWithThreadPreservation(navigate, pathToOpen, searchParams)
                      }
                    >
                      Open related
                    </button>
                  ) : null}
                </li>
              )
            })}
          </ul>
          {hiddenCount > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setExpanded(true)}
            >
              Show more ({hiddenCount})
            </Button>
          ) : null}
          {expanded && rows.length > THREAD_HISTORY_INITIAL_CAP ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(false)}
            >
              Show less
            </Button>
          ) : null}
        </div>
      )}
    </EntityManageSection>
  )
}
